import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Search, X } from 'lucide-react';
import { MSAService } from '@/types';
import { ServiceCard } from '@/components/services/management/common/ServiceCard';
import { SERVICE_CATEGORIES, CATEGORY_LABELS } from '@/constants/serviceCategories';
import { HealthStatus } from '@/types/health';

interface ServiceSearchTabProps {
  searchResults: MSAService[];
  searchLoading: boolean;
  subscribedServiceIds: Set<number>;
  healthStatuses: Record<number, HealthStatus>;
  onSearch: (query: string, category: string) => void;
  onServiceClick: (service: MSAService, event?: React.MouseEvent) => void;
  onSubscribe: (serviceSlug: string) => void;
  onUnsubscribe: (serviceSlug: string) => void;
}

export const ServiceSearchTab: React.FC<ServiceSearchTabProps> = ({
  searchResults,
  searchLoading,
  subscribedServiceIds,
  healthStatuses,
  onSearch,
  onServiceClick,
  onSubscribe,
  onUnsubscribe,
}) => {
  const [searchServiceQuery, setSearchServiceQuery] = useState('');
  const [searchCategory, setSearchCategory] = useState('all');

  const handleSearch = () => {
    // 검색 버튼을 눌렀을 때만 검색 실행
    const category = searchCategory === 'all' ? '' : searchCategory;
    onSearch(searchServiceQuery, category);
  };

  const handleReset = () => {
    setSearchServiceQuery('');
    setSearchCategory('all');
    onSearch('', '');
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-xl font-semibold text-gray-900">전체 서비스 검색</h3>
        <p className="text-gray-600 text-sm">공개 서비스를 검색하고 구독할 수 있습니다</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>서비스 검색</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
              <div className="md:col-span-3">
                <Label>카테고리</Label>
                <Select value={searchCategory || undefined} onValueChange={(value) => setSearchCategory(value || '')}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="전체 카테고리" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem key="search-category-all" value="all">전체 카테고리</SelectItem>
                    {SERVICE_CATEGORIES.map((category) => (
                      <SelectItem key={`search-category-${category}`} value={category}>
                        {CATEGORY_LABELS[category]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-7">
                <Label>검색어</Label>
                <Input
                  type="text"
                  value={searchServiceQuery}
                  onChange={(e) => setSearchServiceQuery(e.target.value)}
                  placeholder="서비스 이름, 설명 또는 개발사 (예: 젠아이랩스)"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                />
              </div>
              <div className="md:col-span-2 flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  disabled={searchLoading}
                  className="flex-1"
                >
                  <X className="mr-2 h-4 w-4" />
                  초기화
                </Button>
                <Button onClick={handleSearch} disabled={searchLoading} className="flex-1">
                  <Search className="mr-2 h-4 w-4" />
                  {searchLoading ? '검색 중...' : '검색'}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {searchResults.length > 0 ? (
        <div className="space-y-4">
          <h4 className="text-lg font-semibold">검색 결과 ({searchResults.length}개)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {searchResults.map((service) => {
              const isSubscribed = subscribedServiceIds.has(service.id);
              return (
                <ServiceCard
                  key={service.id}
                  service={service}
                  healthStatuses={healthStatuses}
                  isSubscribed={isSubscribed}
                  onServiceClick={onServiceClick}
                  onSubscribe={onSubscribe}
                  onUnsubscribe={onUnsubscribe}
                />
              );
            })}
          </div>
        </div>
      ) : !searchLoading ? (
        <div className="text-center text-gray-500 py-8">
          {searchServiceQuery || searchCategory ? '검색 결과가 없습니다.' : '공개 서비스를 불러오는 중...'}
        </div>
      ) : null}
    </div>
  );
};
